import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import type { LocalBucketItem } from '../../db/local.js';
import {
  addBucketItem,
  listBucket,
  markBucketDone,
  overdueBucket,
  planBucketItem,
  removeBucketItem,
  reopenBucketItem,
  shareBucketItem,
} from '../../db/bucket.js';
import { currentHouseholdId } from '../../db/shopping.js';
import { formatDay } from '../../format.js';
import { useToday } from '../../hooks/useToday.js';
import { useMayPlan } from '../../hooks/useModules.js';
import { currentOwnerId } from '../../auth.js';

/**
 * Bucketliste (18.10.2026): Wünsche, keine Aufgaben. Privat sieht ein
 * Eintrag nur, wer ihn angelegt hat — die geheime Liste —, geteilt der
 * Haushalt. „Planen“ legt einen Termin an, nach dem Tag fragt die App
 * „Erlebt?“, und Erlebtes führt in den Tag fürs Foto oder einen Gedanken.
 */
export function BucketList({ onOpen }: { onOpen: (date: string) => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const items = useLiveQuery(() => listBucket(db), [], [] as LocalBucketItem[]);
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const [title, setTitle] = useState('');
  const [shared, setShared] = useState(false);
  const id = useId();
  // Kleine Kinder (E20) lesen die geteilte Liste nur.
  const mayPlan = useMayPlan();
  if (!today) return <></>;

  const ideas = items.filter((item) => item.status === 'idea');
  const planned = items
    .filter((item) => item.status === 'planned')
    .sort((a, b) => (a.eventDate ?? '').localeCompare(b.eventDate ?? ''));
  const done = items
    .filter((item) => item.status === 'done')
    .sort((a, b) => (b.doneDate ?? '').localeCompare(a.doneDate ?? ''));

  return (
    <section aria-labelledby="bucket-heading" className="flex flex-col gap-3">
      <h2 id="bucket-heading" className="sr-only">
        {t('bucket.heading')}
      </h2>
      {mayPlan ? null : (
        <p role="status" className="text-sm text-ink-muted">
          {t('children.level.readOnly')}
        </p>
      )}
      <fieldset disabled={!mayPlan} className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
        <BucketPrompts items={items} today={today} onOpen={onOpen} />

        <form
          className="kw-tile flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim() === '') return;
            const value = title;
            setTitle('');
            void addBucketItem(value, shared && householdId ? householdId : null, db);
          }}
        >
          <p className="text-sm text-ink-muted">{t('bucket.intro')}</p>
          <label htmlFor={id} className="kw-label">
            {t('bucket.title')}
          </label>
          <div className="flex gap-2">
            <input
              id={id}
              value={title}
              maxLength={160}
              onChange={(event) => setTitle(event.target.value)}
              className="kw-field min-w-0 flex-1"
            />
            <button type="submit" className="kw-btn kw-btn-small shrink-0 px-4">
              {t('bucket.add')}
            </button>
          </div>
          {householdId ? (
            <button
              type="button"
              aria-pressed={shared}
              onClick={() => setShared((value) => !value)}
              className="kw-toggle self-start"
            >
              {t('bucket.shareNew')}
            </button>
          ) : null}
        </form>

        <Group heading={t('bucket.ideas')} empty={t('bucket.noIdeas')}>
          {ideas.map((item) => (
            <IdeaRow key={item.id} item={item} today={today} householdId={householdId} />
          ))}
        </Group>
        {planned.length > 0 ? (
          <Group heading={t('bucket.planned')}>
            {planned.map((item) => (
              <li key={item.id} className="flex flex-col gap-1 py-2">
                <ItemTitle item={item} />
                <span className="text-xs text-ink-muted">
                  {t('bucket.plannedFor', {
                    day: formatDay(item.eventDate ?? today, 'short', i18n.language),
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void markBucketDone(item.id, item.eventDate ?? today, db)}
                    className="kw-btn kw-btn-small flex-1"
                  >
                    {t('bucket.done')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void reopenBucketItem(item.id, db)}
                    className="kw-btn kw-btn-quiet kw-btn-small flex-1"
                  >
                    {t('bucket.backToIdea')}
                  </button>
                </div>
              </li>
            ))}
          </Group>
        ) : null}
        {done.length > 0 ? (
          <Group heading={t('bucket.doneHeading')}>
            {done.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-2 py-2">
                <span className="min-w-0">
                  <ItemTitle item={item} />
                  <span className="block text-xs text-ink-muted">
                    {formatDay(item.doneDate ?? today, 'full', i18n.language)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => onOpen(item.doneDate ?? today)}
                  className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
                >
                  {t('bucket.openDay')}
                </button>
              </li>
            ))}
          </Group>
        ) : null}
      </fieldset>
    </section>
  );
}

function Group({
  heading,
  empty,
  children,
}: {
  heading: string;
  empty?: string;
  children: React.ReactNode[];
}): React.JSX.Element {
  return (
    <section className="kw-tile flex flex-col gap-1">
      <h3 className="kw-kicker">{heading}</h3>
      {children.length === 0 && empty ? (
        <p className="text-sm text-ink-muted">{empty}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">{children}</ul>
      )}
    </section>
  );
}

function ItemTitle({ item }: { item: LocalBucketItem }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="block">
      <span className="font-semibold">{item.title}</span>
      <span className="text-xs text-ink-muted">
        {' · '}
        {item.householdId ? t('bucket.shared') : t('bucket.private')}
      </span>
    </span>
  );
}

function IdeaRow({
  item,
  today,
  householdId,
}: {
  item: LocalBucketItem;
  today: string;
  householdId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [planning, setPlanning] = useState(false);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState('');
  const ids = { date: useId(), time: useId() };
  const mine = !item.userId || item.userId === currentOwnerId();

  return (
    <li className="flex flex-col gap-2 py-2">
      <div className="flex items-center justify-between gap-2">
        <ItemTitle item={item} />
        <span className="flex shrink-0 gap-1">
          <button
            type="button"
            aria-expanded={planning}
            onClick={() => setPlanning((value) => !value)}
            aria-label={t('bucket.planNamed', { name: item.title })}
            className="kw-btn kw-btn-small px-3"
          >
            {t('bucket.plan')}
          </button>
          <button
            type="button"
            onClick={() => void removeBucketItem(item.id, db)}
            aria-label={t('bucket.remove', { name: item.title })}
            className="kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
          >
            ×
          </button>
        </span>
      </div>
      {householdId && (item.householdId ? mine : true) ? (
        <button
          type="button"
          onClick={() => void shareBucketItem(item.id, item.householdId ? null : householdId, db)}
          className="self-start text-xs text-ink-muted underline underline-offset-2"
        >
          {item.householdId ? t('bucket.makePrivate') : t('bucket.share')}
        </button>
      ) : null}
      {planning ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (date === '') return;
            void planBucketItem(item.id, date, time || null, db).then(() => setPlanning(false));
          }}
        >
          <label htmlFor={ids.date} className="flex flex-col gap-1">
            <span className="kw-label">{t('schedule.date')}</span>
            <input
              id={ids.date}
              type="date"
              value={date}
              min={today}
              onChange={(event) => setDate(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={ids.time} className="flex flex-col gap-1">
            <span className="kw-label">{t('shops.timeOptional')}</span>
            <input
              id={ids.time}
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              className="kw-field"
            />
          </label>
          <button type="submit" className="kw-btn kw-btn-small px-4">
            {t('bucket.toCalendar')}
          </button>
        </form>
      ) : null}
    </li>
  );
}

/** Geplant und der Tag ist vorbei: „Erlebt?“ — auch auf „Tage“. */
export function BucketPrompts({
  items,
  today,
  onOpen,
}: {
  items: LocalBucketItem[];
  today: string;
  onOpen: (date: string) => void;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const overdue = overdueBucket(items, today);
  if (overdue.length === 0) return null;
  return (
    <>
      {overdue.map((item) => (
        <section key={item.id} className="kw-tile flex flex-col gap-2 border-accent">
          <h3 className="kw-kicker">{t('bucket.askHeading')}</h3>
          <p className="font-semibold">
            {item.title} · {formatDay(item.eventDate!, 'short', i18n.language)}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                void markBucketDone(item.id, item.eventDate!, db).then(() =>
                  onOpen(item.eventDate!),
                )
              }
              className="kw-btn kw-btn-small flex-1"
            >
              {t('bucket.doneAndWrite')}
            </button>
            <button
              type="button"
              onClick={() => void reopenBucketItem(item.id, db)}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('bucket.notYet')}
            </button>
          </div>
        </section>
      ))}
    </>
  );
}
